import { useState, useEffect, useRef } from 'react';
import { useSocket } from '../../context/SocketContext';

async function getTurnConfig() {
  try {
    const res = await fetch('/api/auth/turn-credentials');
    const { username, credential } = await res.json();
    return { iceServers: [
      { urls: 'stun:turn.lepb.fr:3478' },
      { urls: 'turn:turn.lepb.fr:3478', username, credential },
      { urls: 'turns:turn.lepb.fr:5349', username, credential }
    ]};
  } catch {
    return { iceServers: [{ urls: 'stun:turn.lepb.fr:3478' }] };
  }
}

export default function IncomingCallNotif() {
  const [call, setCall] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [callTime, setCallTime] = useState(0);
  const [muted, setMuted] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const socketRef = useSocket();
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const timerRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const callRef = useRef(null);
  const convRef = useRef(null);

  const token = localStorage.getItem('mjtoken');
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const currentUserId = user?._id || user?.id;
  const pseudoMe = user?.username || 'Anonyme';

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { convRef.current = conv; }, [conv]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadMessages = async () => {
    const c = convRef.current;
    if (!token || !c) return;
    const convId = c._id || c.id;
    try {
      const res = await fetch('/api/messages/conversations/' + convId + '/messages', { headers: { Authorization: 'Bearer ' + token } });
      const msgs = await res.json();
      if (Array.isArray(msgs)) setMessages(msgs);
    } catch {}
  };

  const loadConversation = async () => {
    const c = callRef.current;
    if (!token || !c?.fromUserId) return;
    try {
      const res = await fetch('/api/messages/conversations/' + c.fromUserId, { method: 'POST', headers: { Authorization: 'Bearer ' + token } });
      if (!res.ok) return;
      const data = await res.json();
      setConv(data);
      convRef.current = data;
      await loadMessages();
    } catch {}
  };

  useEffect(() => {
    const onIncoming = (data) => setCall(data);
    const onIce = async ({ candidate }) => {
      const pc = pcRef.current;
      if (pc && pc.remoteDescription && pc.remoteDescription.type) {
        try { await pc.addIceCandidate(candidate); } catch (err) { console.error('addIce error', err); }
      } else {
        pendingCandidatesRef.current.push(candidate);
      }
    };
    const onEnded = () => endCall();
    const onNewMessage = ({ fromUserId }) => {
      const c = callRef.current;
      if (c && String(fromUserId) === String(c.fromUserId)) loadMessages();
    };

    const interval = setInterval(() => {
      const socket = socketRef?.current;
      if (!socket?.connected) return;
      socket.off('visio-incoming', onIncoming); socket.on('visio-incoming', onIncoming);
      socket.off('visio-ice', onIce); socket.on('visio-ice', onIce);
      socket.off('visio-ended', onEnded); socket.on('visio-ended', onEnded);
      socket.off('new-message', onNewMessage); socket.on('new-message', onNewMessage);
      clearInterval(interval);
    }, 300);

    return () => {
      clearInterval(interval);
      const socket = socketRef?.current;
      if (socket) {
        socket.off('visio-incoming', onIncoming);
        socket.off('visio-ice', onIce);
        socket.off('visio-ended', onEnded);
        socket.off('new-message', onNewMessage);
      }
    };
  }, [socketRef]);

  useEffect(() => {
    if (inCall && callRef.current?.fromUserId) loadConversation();
  }, [inCall]);

  useEffect(() => {
    const id = inCall ? (callRef.current && callRef.current.fromUserId) : null;
    if (!id) return;
    if (!window.__mjOpenChats) window.__mjOpenChats = new Set();
    window.__mjOpenChats.add(String(id));
    return () => { window.__mjOpenChats && window.__mjOpenChats.delete(String(id)); };
  }, [inCall]);

  const acceptCall = async () => {
    if (!call) return;
    const socket = socketRef?.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localRef.current) localRef.current.srcObject = stream;
      const TURN_CONFIG = await getTurnConfig();
      const pc = new RTCPeerConnection(TURN_CONFIG);
      pcRef.current = pc;
      stream.getTracks().forEach(t => pc.addTrack(t, stream));
      pc.onicecandidate = (e) => {
        if (e.candidate) socket?.emit('visio-ice', { to: call.from, candidate: e.candidate });
      };
      pc.oniceconnectionstatechange = () => console.log('Sissi ICE state:', pc.iceConnectionState);
      pc.ontrack = (e) => {
        if (remoteRef.current) remoteRef.current.srcObject = e.streams[0];
        setInCall(true);
        if (!timerRef.current) timerRef.current = setInterval(() => setCallTime(t => t + 1), 1000);
      };
      await pc.setRemoteDescription(call.offer);

      const buffered = pendingCandidatesRef.current;
      pendingCandidatesRef.current = [];
      for (const c of buffered) {
        try { await pc.addIceCandidate(c); } catch (err) { console.error('flush addIce error', err); }
      }

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket?.emit('visio-answer', { to: call.from, answer });
    } catch (e) {
      console.error('acceptCall error:', e);
      setCall(null);
    }
  };

  const refuseCall = () => {
    socketRef?.current?.emit('visio-refuse', { to: call?.from });
    setCall(null);
  };

  const toggleMute = () => {
    if (streamRef.current) streamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    setMuted(m => !m);
  };

  const envoyer = async () => {
    const c = convRef.current;
    if ((!input.trim() && !file) || !c || sending) return;
    setSending(true);
    const convId = c._id || c.id;
    const fd = new FormData();
    if (input.trim()) fd.append('text', input);
    if (file) fd.append('image', file);
    try {
      const res = await fetch('/api/messages/conversations/' + convId + '/messages', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        const socket = socketRef?.current;
        const peerId = callRef.current?.fromUserId;
        if (socket && peerId) {
          socket.emit('new-message', { toUserId: peerId, fromUserId: currentUserId, fromPseudo: pseudoMe, preview: input ? input.slice(0, 60) : 'Image' });
        }
        setInput('');
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
      }
    } catch {}
    setSending(false);
  };

  const endCall = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    streamRef.current = null;
    pendingCandidatesRef.current = [];
    setMuted(false);
    setInCall(false);
    setCall(null);
    setCallTime(0);
    setConv(null);
    setMessages([]);
    setInput('');
    setFile(null);
  };

  const hangup = () => {
    socketRef?.current?.emit('visio-end', { to: call?.from });
    endCall();
  };

  const fmt = (s) => String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');

  if (!call && !inCall) return null;

  const pseudo = call?.pseudo || 'Un membre';
  const initial = pseudo.charAt(0).toUpperCase();

  return (
    <div style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 9999, background: '#1a0808', border: '1px solid #8b1a2a', borderRadius: 12, width: 300, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#5a1020', padding: '8px 12px' }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#8b1a2a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia,serif', fontSize: 14, flexShrink: 0 }}>{initial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontFamily: 'Georgia,serif', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pseudo}</div>
          <div style={{ color: '#7ed07e', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3fb950', display: 'inline-block' }} /> En ligne</div>
        </div>
        <button onClick={inCall ? hangup : refuseCall} title="Fermer" style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 15, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
      </div>

      <div style={{ padding: 12 }}>
        <div style={{ display: inCall ? 'block' : 'none' }}>
          <div style={{ background: '#0d0808', borderRadius: 8, overflow: 'hidden', marginBottom: 8, position: 'relative', height: 150 }}>
            <video ref={remoteRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <video ref={localRef} autoPlay muted playsInline style={{ position: 'absolute', bottom: 6, right: 6, width: 50, height: 38, objectFit: 'cover', borderRadius: 4, border: '1px solid rgba(255,255,255,0.3)' }} />
            <div style={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 12, padding: '2px 6px', borderRadius: 6 }}>{fmt(callTime)}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button onClick={toggleMute} style={{ flex: 1, padding: '7px', borderRadius: 8, border: '1px solid #8b1a2a', background: muted ? '#8b1a2a' : 'transparent', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{muted ? 'Micro coupe' : 'Couper le micro'}</button>
            <button onClick={hangup} style={{ flex: 1, padding: '7px', borderRadius: 8, border: 'none', background: '#c03030', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Raccrocher</button>
          </div>

          <div style={{ height: 140, overflowY: 'auto', padding: '6px 4px', display: 'flex', flexDirection: 'column', gap: 6, background: '#120606', borderRadius: 8, marginBottom: 8 }}>
            {messages.length === 0 && <div style={{ textAlign: 'center', color: '#7a5555', fontSize: 12, marginTop: 16, fontStyle: 'italic' }}>Debut de la conversation…</div>}
            {messages.map((m, i) => {
              const isMe = String(m.sender?._id || m.sender) === String(currentUserId);
              const heure = m.createdAt ? new Date(m.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
              return (
                <div key={m._id || i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '78%', background: isMe ? '#8b1a2a' : '#2a1414', color: '#fff', borderRadius: isMe ? '10px 10px 3px 10px' : '10px 10px 10px 3px', padding: '6px 10px', fontSize: 12, lineHeight: 1.4 }}>
                    {m.text}
                    {m.imageUrl && <img src={m.imageUrl} alt="" style={{ maxWidth: 140, borderRadius: 6, display: 'block', marginTop: m.text ? 4 : 0 }} />}
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2, textAlign: 'right' }}>{heure}</div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={() => fileRef.current?.click()} title="Envoyer une image" style={{ background: 'none', border: 'none', color: '#e8a0a0', fontSize: 18, cursor: 'pointer', padding: 0 }}>📷</button>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && envoyer()} placeholder="Ecrire un message…" disabled={!conv} style={{ flex: 1, padding: '7px 12px', borderRadius: 18, border: '1px solid #8b1a2a', background: '#0d0808', color: '#fff', fontSize: 12, outline: 'none' }} />
            <button onClick={envoyer} disabled={!conv || sending} style={{ padding: '7px 12px', borderRadius: 18, border: 'none', background: '#8b1a2a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (!conv || sending) ? 0.6 : 1 }}>→</button>
          </div>
          {file && <div style={{ fontSize: 12, color: '#e8a0a0', marginTop: 4 }}>Image prete : {file.name}</div>}
        </div>

        {call && !inCall && (
          <div>
            <div style={{ fontSize: 12, color: '#e8a0a0', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>VIDEO APPEL ENTRANT</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>vous appelle en visio...</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={acceptCall} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#1a6a30', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Accepter</button>
              <button onClick={refuseCall} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: '#c03030', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Refuser</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
